import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
  HttpResponse,
  HttpUrlEncodingCodec,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forOwn, identity, isEmpty, pickBy } from 'lodash';
import { Observable, throwError } from 'rxjs';
import {
  catchError,
  finalize,
  map,
  share,
  timeout as rxTimeout,
} from 'rxjs/operators';
import { v4 } from 'uuid';
import {
  CONTENT_TYPE_HEADER,
  CORRELATION_ID_KEY,
  IHttpService,
  IRequest,
  IResponseInfo,
  LONG_REQUEST_TIMEOUT,
  REQUEST_TIMEOUT,
  RequestMethod,
  ResponseContentType,
} from './http.data';
import { ErrorHandlerService } from '../error-handler/error-handler.service';

export class HttpQueryEncoder extends HttpUrlEncodingCodec {
  encodeValue(v: string): string {
    return encodeURIComponent(v);
  }
}

@Injectable({
  providedIn: 'root',
})
export class HttpService implements IHttpService {
  private baseHeaders: any;
  private _outboundRequests: {
    [id: string]: Observable<HttpResponse<any>>;
  } = {};

  constructor(
    private _httpClient: HttpClient,
    private _errorService: ErrorHandlerService
  ) {}

  setBaseHeaders(baseHeaders: any) {
    this.baseHeaders = baseHeaders;
  }

  request(
    method: RequestMethod,
    url: string,
    body: any,
    headers: any = {},
    storeRequest?: boolean,
    responseContentType?: ResponseContentType,
    withCredentials?: boolean
  ): Observable<IResponseInfo> {
    switch (method) {
      case 'GET':
        return this.get(
          url,
          headers,
          body,
          responseContentType,
          false,
          withCredentials
        );
      case 'POST':
        return this.post(
          url,
          body,
          headers,
          storeRequest,
          responseContentType,
          withCredentials
        );
      case 'PUT':
        return this.put(
          url,
          body,
          headers,
          storeRequest,
          responseContentType,
          withCredentials
        );
      case 'DELETE':
        return this.delete(url, body, headers, storeRequest, withCredentials);
      default:
        return throwError(new Error(`Method not supported: ${method}`));
    }
  }

  get(
    url: string,
    headers: any = {},
    params: any = null,
    responseContentType: ResponseContentType = 'json',
    externalUrl: boolean = false,
    withCredentials: boolean = false
  ): Observable<IResponseInfo> {
    return this.handleRequest({
      method: 'GET',
      url,
      params,
      headers,
      responseType: responseContentType,
      timeout:
        responseContentType === 'json' ? REQUEST_TIMEOUT : LONG_REQUEST_TIMEOUT,
      withCredentials,
    });
  }

  post(
    url: string,
    body: any = {},
    headers: any = {},
    storeRequest: boolean = true,
    responseContentType: ResponseContentType = 'json',
    withCredentials: boolean = false
  ): Observable<IResponseInfo> {
    return this.handleRequest({
      method: 'POST',
      url,
      body,
      headers,
      storeRequest,
      responseType: responseContentType,
      withCredentials,
    });
  }

  put(
    url: string,
    body: any = {},
    headers: any = {},
    storeRequest: boolean = true,
    responseContentType: ResponseContentType = 'json',
    withCredentials: boolean = false
  ): Observable<IResponseInfo> {
    return this.handleRequest({
      method: 'PUT',
      url,
      body,
      headers,
      storeRequest,
      responseType: responseContentType,
      withCredentials,
    });
  }

  delete(
    url: string,
    body: any = {},
    headers: any = {},
    storeRequest: boolean = true,
    withCredentials: boolean = false
  ): Observable<IResponseInfo> {
    return this.handleRequest({
      method: 'DELETE',
      url,
      body,
      headers,
      storeRequest,
      responseType: 'json',
      withCredentials,
    });
  }

  anyOutboundRequests() {
    return !isEmpty(this._outboundRequests);
  }

  handleRequest({
    method,
    url,
    body,
    params,
    headers,
    storeRequest = false,
    responseType = 'json',
    timeout = REQUEST_TIMEOUT,
    withCredentials = false,
  }: IRequest): Observable<any> {
    const requestId = v4();
    const requestHeaders: HttpHeaders = this.getRequestHeaders(headers);
    const req = this._httpClient.request(method, url, {
      headers: requestHeaders,
      params: params ? new HttpParams({ fromObject: params }) : null,
      body: this.getPayload(body, requestHeaders),
      observe: 'response',
      withCredentials,
      responseType,
    });

    if (storeRequest) {
      this._outboundRequests[requestId] = req;
    }

    return req.pipe(
      rxTimeout(timeout),
      map(
        (response: HttpResponse<any>): IResponseInfo => {
          const responseHeaders: HttpHeaders = response.headers;

          switch (responseType) {
            case 'json':
            case 'blob':
              return {
                headers: responseHeaders,
                body: response.body,
                status: response.status,
              };
            case 'arraybuffer':
              return {
                headers: responseHeaders,
                body: [responseHeaders.get(CONTENT_TYPE_HEADER), response.body],
                status: response.status,
              };
            default:
              return {
                headers: responseHeaders,
                body: {},
                status: response.status,
              };
          }
        }
      ),
      catchError((error: HttpErrorResponse) => {
        this._errorService.alertSystem(error, {
          method,
          url,
          body,
          headers,
          storeRequest,
          responseType,
          timeout,
        });

        return throwError(error);
      }),
      finalize(() => delete this._outboundRequests[requestId]),
      share()
    );
  }

  getRequestHeaders(headers: any = {}): HttpHeaders {
    const correlationId = v4();

    const commonHeaders = {
      [CONTENT_TYPE_HEADER]: 'application/json; charset=utf-8',
      [CORRELATION_ID_KEY]: correlationId,
    };

    return new HttpHeaders(
      pickBy(
        {
          ...commonHeaders,
          ...this.baseHeaders,
          ...headers,
        },
        identity
      )
    );
  }

  getPayload(body: any, headers?: HttpHeaders): any {
    if (
      body &&
      headers &&
      headers.get(CONTENT_TYPE_HEADER) === 'application/x-www-form-urlencoded'
    ) {
      let payload = new HttpParams({
        fromString: '',
        encoder: new HttpQueryEncoder(),
      });

      forOwn(
        body,
        (value: string, key: string) => (payload = payload.append(key, value))
      );

      return payload;
    }

    return body;
  }
}
